import {DEFAULT_VALUE, INIT_INDEX, ONE_ELEMENT} from 'util/constants';
import {getSortParam, getUrlParameters, getNow, getErrorMessage} from 'util/utils';
import BadRequestError from 'util/api/errors/BadRequestError';
import BadRequestFormError from 'util/api/errors/BadRequestFormError';

import {getApi} from 'modules/api';
import {addNotification, NOTIFICATION_TYPE} from 'modules/notifications';

export const
    NOT_ERROR = null,
    DEFAULT_DATA = [];
const DOWNLOAD_VALUE = 1;

export default class StoreModuleUtils {
    static extraActionConstants = {
        start:   'start',
        success: 'success',
        error:   'error'
    };
    static methods = {
        fetch:         'fetch',
        fetchInstance: 'fetchInstance',
        update:        'update',
        create:        'create',
        delete:        'delete'
    };

    static createActionTypes(name) {
        if (!name) {
            throw new Error('name does not exist');
        }

        return [
            'INVALIDATE',
            'STORE_ERROR', 'ENTITIES_ERROR', 'ENTITY_ERROR', 'CLEAR_ERROR', 'CLEAR_ENTITY_ERROR',
            'CLEAR_ENTITIES_ERROR', 'FETCH_START', 'FETCH_SUCCESS', 'SET_ENTITIES',
            'FETCH_INSTANCE_START', 'FETCH_INSTANCE_SUCCESS', 'SET_ID_CACHE',
            'CREATE_START', 'CREATE_SUCCESS',
            'UPDATE_START', 'UPDATE_SUCCESS',
            'DELETE_START', 'DELETE_SUCCESS'
        ].reduce((actionTypes, operation) => ({
            ...actionTypes,
            [operation]: `${name.toUpperCase()}_${operation}`
        }), {});
    }

    static capitalize(string) {
        if (!string) {
            throw new Error('string does not exist');
        }

        return string.charAt(INIT_INDEX).toUpperCase() + string.slice(ONE_ELEMENT);
    }

    static transformResultsFn({results}) {
        return results;
    }

    static transformEntitiesFn(entity) {
        return entity;
    }

    static transformEntitiesToSaveFn(entity) {
        return entity;
    }

    static getFiltersKey(par) {
        if (par instanceof Array) {
            return par.map(StoreModuleUtils.getFiltersKey)
                .sort()
                .join(',');
        } else if (typeof par === 'object' && par !== null) {
            return Object.keys(par).sort()
                .map(key => `${key}=${StoreModuleUtils.getFiltersKey(par[key])}`)
                .join(';');
        }

        return typeof par !== 'undefined' ? par : '';
    }

    constructor(props = {}) {
        this.name = props.name;
        this.endpoint = props.endpoint;
        this.instanceEndpoint = props.instanceEndpoint;
        this.idField = props.idField || 'id';
        this.transformResultsFn = props.transformResultsFn || StoreModuleUtils.transformResultsFn;
        this.transformEntitiesFn = props.transformEntitiesFn || StoreModuleUtils.transformEntitiesFn;
        this.transformEntitiesToSaveFn = props.transformEntitiesToSaveFn || StoreModuleUtils.transformEntitiesToSaveFn;
        this.extraState = props.extraState;
        this.extraActions = props.extraActions;
        this.callbackActions = props.callbackActions;
        this.defaultParameters = props.defaultParameters || {};
        this.timeout = props.timeout === undefined ? false : props.timeout;
        this.parseParameters = props.parseParameters || (a => a);
        this.filterParamsMap = {
            ...props.filterParamsMap || {},
            pageSize: 'page_size'
        };
        this.sortParamsMap = props.sortParamsMap || {};

        this.actionTypes = StoreModuleUtils.createActionTypes(this.name);
    }

    static getEntitiesState() {
        return {
            isFetching: false,
            error:      NOT_ERROR,
            total:      DEFAULT_VALUE,
            data:       DEFAULT_DATA,
            next:       null
        };
    }

    static getEntityState() {
        return {
            isFetching: false,
            error:      NOT_ERROR,
            data:       null
        };
    }

    getInitialState() {
        return {
            didInvalidate: false,
            isCreating:    false,
            isUpdating:    false,
            isDeleting:    false,
            error:         NOT_ERROR,
            cache:         {
                entities: {},
                entity:   {}
            },
            ...this.extraState
        };
    }

    getEntities(state, filters) {
        const
            key = StoreModuleUtils.getFiltersKey(filters),
            cacheValue = state.store[this.name].cache.entities[key];

        return {
            ...StoreModuleUtils.getEntitiesState(),
            didInvalidate: state.store[this.name].didInvalidate,
            ...cacheValue
        };
    }

    getEntity(state, id, test) {
        /*if (test) {
            console.log('data entity: ', state.store[this.name].cache.entity[id]);
            console.log('id entity: ', id);
            console.log('state entity: ', state);
        }*/
        const _getBasic = () => ({
            ...StoreModuleUtils.getEntityState(),
            didInvalidate: state.store[this.name].didInvalidate
        });

        return {
            ..._getBasic(),
            ...state.store[this.name].cache.entity[id]
        };
    }

    parseParams(params) {
        const _parseParameter = key => {
            if (key === 'ordering') {
                const field = this.sortParamsMap[params[key].field] || params[key].field;

                return {
                    [key]: getSortParam({
                        ...params[key],
                        field
                    })
                };
            }

            return {
                [this.filterParamsMap[key] || key]: params[key]
            };
        };

        return Object.keys(params).reduce((ret, key) => ({
            ...ret,
            ..._parseParameter(key)
        }), {});
    }

    getDownloadUrl(filters) {
        return this.createUrl({
            ...filters,
            download: DOWNLOAD_VALUE
        });
    }

    createUrl(filters) {
        const _replaceParameters = endpoint => {
            const indexParam = endpoint.indexOf(':');
            const PARAM_INIT_SIZE = 1;

            if (~indexParam) {
                const auxText = endpoint.substring(indexParam);
                let indexEnd = auxText.indexOf('/');

                if (!~indexEnd) {
                    indexEnd = auxText.length;
                }

                const param = auxText.substring(PARAM_INIT_SIZE, indexEnd);
                const finalEndpoint = endpoint.replace(`:${param}/`, `${filters[param]}/`);

                delete filters[param];

                return _replaceParameters(finalEndpoint);
            }

            return endpoint;
        };

        return getUrlParameters(_replaceParameters(this.endpoint), {
            ...this.defaultParameters,
            ...this.parseParams(this.parseParameters(filters))
        });
    }

    manageError(action) {
        return dispatch => {
            getErrorMessage(action.error)
                .then(msg => {

                    const _addNotification = (title, text, translatable) => {
                        if (text instanceof Array) {
                            text.forEach(msgItem => _addNotification(title, msgItem, translatable));
                        } else if (typeof text === 'object') {
                            Object.keys(text).forEach(key => {
                                _addNotification(`${title} - ${key}`, text[key], false);
                            });
                        } else {

                            dispatch(addNotification(title, text, NOTIFICATION_TYPE.ERROR, true, translatable));
                        }
                    };

                    _addNotification(this.name, msg, true);
                })
                .catch(e => {
                    throw e;
                });

            return dispatch(action);
        };
    }

    invalidate() {
        return {
            type: this.actionTypes.INVALIDATE
        };
    }

    setEntities(entities, filters, receivedAt) {
        return {
            type:  this.actionTypes.SET_ENTITIES,
            total: entities.length,
            receivedAt, entities, filters
        };
    }

    storeError(error, launchedAction) {
        const action = {
            type: this.actionTypes.STORE_ERROR,
            error, launchedAction
        };

        return dispatch => {
            if (error.response) {
                return error.response.json()
                    .then(json => {
                        if (error instanceof BadRequestError) {
                            action.error = new BadRequestFormError(error.response, json);

                            return dispatch(action);
                        }

                        error.responseBackend = json;

                        return dispatch(this.manageError(action));
                    })
                    .catch(e => dispatch(this.manageError(action)));
            }

            return dispatch(this.manageError(action));
        };
    }

    entitiesError(error, key) {
        const action = {
            type: this.actionTypes.ENTITIES_ERROR,
            error, key
        };

        return dispatch => {
            if (error.response) {
                return error.response.json()
                    .then(json => {
                        error.responseBackend = error.responseBackend || json;

                        return dispatch(this.manageError(action));
                    })
                    .catch(e => dispatch(this.manageError(action)));
            }

            return dispatch(this.manageError(action));
        };
    }

    clearEntitiesError(filters) {
        return {
            type: this.actionTypes.CLEAR_ENTITIES_ERROR,
            key:  StoreModuleUtils.getFiltersKey(filters)
        };
    }

    entityError(error, id) {
        const action = {
            type: this.actionTypes.ENTITY_ERROR,
            error, id
        };

        return dispatch => {
            if (error.response) {
                return error.response.json()
                    .then(json => {
                        error.responseBackend = json;

                        return dispatch(this.manageError(action));
                    })
                    .catch(e => dispatch(this.manageError(action)));
            }

            return dispatch(this.manageError(action));
        };
    }

    clearError() {
        return {
            type: this.actionTypes.CLEAR_ERROR
        };
    }

    clearEntityError(id) {
        return {
            type: this.actionTypes.CLEAR_ENTITY_ERROR,
            id
        };
    }

    fetchIfNeeded(filters = {}) {
        return (dispatch, getState) => {
            const state = getState();

            if (this.shouldFetch(state.store, filters)) {
                return dispatch(this.fetch(filters));
            }

            return Promise.resolve();
        };
    }

    shouldFetch(state, filters = {}) {
        if (!state) {
            throw new Error('state does not exist');
        }

        const cache = this.getEntities({store: state}, filters);

        return !cache || (!cache.isFetching && (state.didInvalidate || (cache.data instanceof Array && (!cache.data.length || (this.timeout !== false && cache.lastUpdated + this.timeout < getNow())))));
    }

    fetch(filters = {}, throwError) {
        return (dispatch, getState) => {
            if (getState().auth.isLoggedIn) {
                dispatch(this.fetchStart(filters));

                const url = this.createUrl({...filters});

                return getApi().get(url)
                    .then(json => {
                        if (this.callbackActions) {
                            this.callbackActions.forEach(callbackAction => {
                                callbackAction.method === StoreModuleUtils.methods.fetch && dispatch(callbackAction.action(json));
                            });
                        }

                        return dispatch(this.fetchSuccess(filters, json));
                    })
                    .catch(error => {
                        dispatch(this.entitiesError(error, StoreModuleUtils.getFiltersKey(filters)));

                        if (throwError) {
                            throw error;
                        } else {
                            console.error(error);
                        }
                    });
            }

            return Promise.resolve();

        };
    }

    fetchStart(filters) {
        return {
            type: this.actionTypes.FETCH_START,
            filters
        };
    }

    fetchSuccess(filters, json) {
        return {
            type:       this.actionTypes.FETCH_SUCCESS,
            entities:   this.transformResultsFn(json, filters),
            receivedAt: Date.now(),
            total:      json.count,
            next:       json.next,
            filters
        };
    }

    fetchInstanceIfNeeded(id, extraParams, throwError) {

        return (dispatch, getState) => {
            const state = getState();

            return dispatch(this.shouldFetchInstance(state.store, id) ?
                this.fetchInstance(id, extraParams, throwError) :
                this.setIdCache(id));
        };
    }

    shouldFetchInstance(state, id) {
        if (!state) {
            throw new Error('state does not exist');
        }

        const cache = this.getEntity({store: state}, id);

        return !cache || (!cache.isFetching && (state.didInvalidate || !cache.entity || (this.timeout !== false && cache.lastUpdated + this.timeout < getNow())));
    }

    setIdCache(id) {
        return (dispatch, getState) => {
            const
                state = getState(),
                {name} = this;

            return dispatch({
                type:  this.actionTypes.SET_ID_CACHE,
                cache: state.store[name].cache.entity[id]
            });
        };
    }

    fetchInstance(id, extraParams, throwError, mikey) {
        // debugger;

        return (dispatch, getState) => {
            if (getState().auth.isLoggedIn) {
                dispatch(this.fetchInstanceStart(id));

                let url = this.instanceEndpoint.replace(':id', id);

                if (extraParams) {
                    url = `${url}?`;
                    Object.keys(extraParams).forEach((key, index) => {
                        //url = url.replace(`:${key}`, extraParams[key]);
                        if (index === INIT_INDEX) {
                            url = `${url + key}=${extraParams[key]}`;
                        } else {
                            url = `${url}&${key}=${extraParams[key]}`;
                        }
                    });
                }

                if (extraParams && extraParams.awsShow) {

                    url = url.replace(':accountId', extraParams.accountId);
                    url = url.replace(':ticketId', id);
                }

                return getApi().get(url)
                    .then(json => {
                        if (this.callbackActions) {
                            this.callbackActions.forEach(callbackAction => {
                                callbackAction.method === StoreModuleUtils.methods.fetchInstance && dispatch(callbackAction.action(json));
                            });
                        }

                        return dispatch(this.fetchInstanceSuccess(json, id));
                    })
                    .catch(error => {

                        dispatch(this.entityError(error, id));

                        if (throwError) {
                            throw error;
                        } else {
                            console.error(error);
                        }
                    });
            }

            return Promise.resolve();

        };
    }

    fetchInstanceStart(id) {
        //debugger;
        return {
            type: this.actionTypes.FETCH_INSTANCE_START,
            id
        };
    }

    fetchInstanceSuccess(json, id) {
        // debugger;
        return {
            type:       this.actionTypes.FETCH_INSTANCE_SUCCESS,
            entity:     json,
            receivedAt: Date.now(),
            id
        };
    }

    create(data, throwError) {
        return dispatch => {
            // debugger;
            const transformed = this.transformEntitiesToSaveFn(data);


            dispatch(this.createStart());

            let url = this.endpoint;

            if (data) {
                Object.keys(data).forEach(key => {
                    url = url.replace(`:${key}`, data[key]);
                });
            }

            return getApi().post(url, transformed)
                .then(json => {
                    if (this.callbackActions) {
                        this.callbackActions.forEach(callbackAction => {
                            callbackAction.method === StoreModuleUtils.methods.create && dispatch(callbackAction.action(json));
                        });
                    }

                    dispatch(this.createSuccess(json));

                    return json;
                })
                .catch(error => {
                    dispatch(this.storeError(error, 'isCreating'));


                    if (throwError) {
                        throw error;
                    } else {
                        console.error(error);
                    }
                });
        };
    }

    createStart() {
        return {
            type: this.actionTypes.CREATE_START
        };
    }

    createSuccess(json) {
        return {
            type:   this.actionTypes.CREATE_SUCCESS,
            entity: json
        };
    }

    update(data, throwError) {
        if (!this.idField) {
            throw new Error('An update action needs an idField');
        }

        return dispatch => {
            const transformed = this.transformEntitiesToSaveFn(data);

            dispatch(this.updateStart());

            return getApi().put(this.instanceEndpoint.replace(':id', data[this.idField]), transformed)
                .then(json => {
                    if (this.callbackActions) {
                        this.callbackActions.forEach(callbackAction => {
                            callbackAction.method === StoreModuleUtils.methods.update && dispatch(callbackAction.action(json));
                        });
                    }

                    return dispatch(this.updateSuccess(json));
                })
                .catch(error => {
                    dispatch(this.storeError(error, 'isUpdating'));

                    if (throwError) {
                        throw error;
                    } else {
                        console.error(error);
                    }
                });
        };
    }

    updateStart(data) {
        return {
            type: this.actionTypes.UPDATE_START,
            data
        };
    }

    updateSuccess(json) {
        return {
            type:   this.actionTypes.UPDATE_SUCCESS,
            entity: json
        };
    }

    delete(id, throwError) {
        return dispatch => {
            dispatch(this.deleteStart(id));

            return getApi().delete_(this.instanceEndpoint.replace(':id', id))
                .then(json => {
                    if (this.callbackActions) {
                        this.callbackActions.forEach(callbackAction => {
                            callbackAction.method === StoreModuleUtils.methods.delete && dispatch(callbackAction.action(json));
                        });
                    }

                    return dispatch(this.deleteSuccess(json));
                })
                .catch(error => {
                    dispatch(this.storeError(error, 'isDeleting'));

                    if (throwError) {
                        throw error;
                    } else {
                        console.error(error);
                    }
                });
        };
    }

    deleteStart(id) {
        return {
            type: this.actionTypes.DELETE_START,
            id
        };
    }

    deleteSuccess() {
        return {
            type: this.actionTypes.DELETE_SUCCESS
        };
    }

    createUpdateStartExtraAction(action, data) {
        return {
            type: this.createExtraActionConstant(action, StoreModuleUtils.extraActionConstants.start),
            data
        };
    }

    createCreateStartExtraAction(action, data) {
        return {
            type: this.createExtraActionConstant(action, StoreModuleUtils.extraActionConstants.start),
            data
        };
    }

    createFetchStartExtraAction(action, id) {
        return {
            type: this.createExtraActionConstant(action, StoreModuleUtils.extraActionConstants.start),
            id
        };
    }

    createDeleteStartExtraAction(action, id) {
        return {
            type: this.createExtraActionConstant(action, StoreModuleUtils.extraActionConstants.start),
            id
        };
    }

    createUpdateSuccessExtraAction(action, json) {
        return {
            type:   this.createExtraActionConstant(action, StoreModuleUtils.extraActionConstants.success),
            entity: json
        };
    }

    createCreateSuccessExtraAction(action, json) {
        return {
            type: this.createExtraActionConstant(action, StoreModuleUtils.extraActionConstants.success),
            data: json
        };
    }

    createFetchSuccessExtraAction(action, json, id) {
        return {
            type:     this.createExtraActionConstant(action, StoreModuleUtils.extraActionConstants.success),
            entities: json,
            id
        };
    }

    createDeleteSuccessExtraAction(action, id) {
        return {
            type: this.createExtraActionConstant(action, StoreModuleUtils.extraActionConstants.success),
            id
        };
    }

    createFetchErrorExtraAction(action, error) {
        return {
            type: this.createExtraActionConstant(action, StoreModuleUtils.extraActionConstants.error),
            error
        };
    }

    createUpdateExtraAction(action) {
        return (id, data, throwError) => dispatch => {
            dispatch(this.createUpdateStartExtraAction(action.action, data));
            const transformed = data && (typeof action.transform === 'function' ? action.transform(data) : this.transformEntitiesToSaveFn(data));

            let {url} = action;

            if (typeof id === 'object') {
                Object.keys(id).forEach(key => {
                    url = url.replace(`:${key}`, id[key]);
                });
            } else if (typeof id === 'string' || typeof id === 'number') {
                url = url.replace(':id', id);
            }

            return getApi().put(url, transformed)
                .then(json => dispatch(this.createUpdateSuccessExtraAction(action.action, json)))
                .catch(error => {
                    dispatch(this.storeError(error, 'isUpdating'));

                    if (throwError) {
                        throw error;
                    } else {
                        console.error(error);
                    }
                });
        };
    }

    createCreateExtraAction(action) {
        return (data, throwError) => dispatch => {
            dispatch(this.createCreateStartExtraAction(action.action, data));
            const
                url = action.url || this.instanceEndpoint + action.action,
                transformed = this.transformEntitiesToSaveFn(data);

            return getApi().post(url.replace(':id/', ''), transformed)
                .then(json => dispatch(this.createCreateSuccessExtraAction(action.action, json)))
                .catch(error => {
                    dispatch(this.storeError(error, 'isCreating'));

                    if (throwError) {
                        throw error;
                    } else {
                        console.error(error);
                    }
                });
        };
    }

    createFetchExtraAction(action) {
        return (id, throwError) => dispatch => {
            dispatch(this.createFetchStartExtraAction(action.action, id));
            const url = action.url || this.instanceEndpoint + action.action;

            return getApi().get(url.replace(':id', id))
                .then(json => dispatch(this.createFetchSuccessExtraAction(action.action, json, id)))
                .catch(error => {
                    dispatch(this.createFetchErrorExtraAction(action.action, error));
                    dispatch(this.storeError(error));

                    if (throwError) {
                        throw error;
                    } else {
                        console.error(error);
                    }
                });
        };
    }

    createDeleteExtraAction(action) {
        return (id, throwError) => dispatch => {
            dispatch(this.createDeleteStartExtraAction(action.action, id));
            const url = action.url || this.instanceEndpoint + action.action;

            return getApi().delete_(url.replace(':id', id))
                .then(() => dispatch(this.createDeleteSuccessExtraAction(action.action, id)))
                .catch(error => {
                    dispatch(this.storeError(error, 'isDeleting'));

                    if (throwError) {
                        throw error;
                    } else {
                        console.error(error);
                    }
                });
        };
    }

    createExtraActionConstant(action, innerAction) {
        return [this.name.toUpperCase(), action.toUpperCase(), innerAction.toUpperCase()].join('_');
    }

    createExtraActions() {
        if (this.extraActions) {
            return this.extraActions.reduce((newObj, item, index) => ({
                ...newObj,
                [item.action + StoreModuleUtils.capitalize(this.name)]: item.method === StoreModuleUtils.methods.update ? this.createUpdateExtraAction(item) :
                    item.method === StoreModuleUtils.methods.fetch ? this.createFetchExtraAction(item) :
                        item.method === StoreModuleUtils.methods.create ? this.createCreateExtraAction(item) :
                            undefined
            }), {});
        }

        return {};
    }

    reducerExtraActions(state, action = {}) {
        if (this.extraActions) {
            const
                _isStart = extraAction => action.type === this.createExtraActionConstant(extraAction.action, StoreModuleUtils.extraActionConstants.start),
                _isSuccess = extraAction => action.type === this.createExtraActionConstant(extraAction.action, StoreModuleUtils.extraActionConstants.success),
                _isError = extraAction => action.type === this.createExtraActionConstant(extraAction.action, StoreModuleUtils.extraActionConstants.error),
                extraAction = this.extraActions.find(item => _isStart(item) || _isSuccess(item) || _isError(item));

            if (extraAction) {
                if (_isStart(extraAction)) {
                    if (typeof extraAction.start === 'function') {
                        return extraAction.start(state, action);
                    }

                    switch (extraAction.method) {
                    case StoreModuleUtils.methods.update:
                        return {
                            ...state,
                            isUpdating: true
                        };
                    case StoreModuleUtils.methods.create:
                        return {
                            ...state,
                            isCreating: true
                        };
                    case StoreModuleUtils.methods.delete:
                        return {
                            ...state,
                            isDeleting: true
                        };
                    default:
                        return state;
                    }
                } else if (_isSuccess(extraAction)) {
                    if (typeof extraAction.success === 'function') {
                        return extraAction.success(state, action);
                    }

                    switch (extraAction.method) {
                    case StoreModuleUtils.methods.update:
                        return {
                            ...state,
                            ...this.getInitialState(),
                            isUpdating:    false,
                            error:         NOT_ERROR,
                            didInvalidate: true
                        };
                    case StoreModuleUtils.methods.create:
                        return {
                            ...state,
                            ...this.getInitialState(),
                            isCreating:    false,
                            error:         NOT_ERROR,
                            didInvalidate: true
                        };
                    case StoreModuleUtils.methods.delete:
                        return {
                            ...state,
                            ...this.getInitialState(),
                            isDeleting:    false,
                            error:         NOT_ERROR,
                            didInvalidate: true
                        };
                    default:
                        return state;
                    }
                } else if (_isError(extraAction)) {
                    if (typeof extraAction.error === 'function') {
                        return extraAction.error(state, action);
                    }
                }
            }
        }

        return state;
    }

    reducer(state, action = {}) {
        const
            _state = state || this.getInitialState(),
            _fetchEntities = newState => {
                const filtersKey = StoreModuleUtils.getFiltersKey(action.filters);
                const ret = {
                    ..._state,
                    didInvalidate: false,
                    cache:         {
                        ..._state.cache,
                        entities: {
                            ..._state.cache.entities,
                            [filtersKey]: {
                                ...StoreModuleUtils.getEntitiesState(),
                                ..._state.cache.entities[filtersKey],
                                error: NOT_ERROR,
                                ...newState
                            }
                        }
                    }
                };

                return ret;
            },
            _setEntities = () => _fetchEntities({
                isFetching:  false,
                lastUpdated: action.receivedAt,
                total:       action.total,
                data:        action.entities
            }),
            _fetchEntitiesStart = () => _fetchEntities({
                isFetching: true
            }),
            _fetchEntitiesSuccess = () => _fetchEntities({
                isFetching:  false,
                lastUpdated: action.receivedAt,
                total:       action.total,
                next:        action.next,
                data:        action.entities ? action.entities instanceof Array ? action.entities.map(this.transformEntitiesFn) : action.entities : DEFAULT_DATA
            }),
            _fetchEntity = newState => {
                const
                    ret = {
                        ..._state,
                        didInvalidate: false,
                        cache:         {
                            ..._state.cache,
                            entity: {
                                ..._state.cache.entity,
                                [action.id]: {
                                    ...StoreModuleUtils.getEntityState(),
                                    ..._state.cache.entity[action.id],
                                    error: NOT_ERROR,
                                    ...newState
                                }
                            }
                        }
                    };

                return ret;
            },
            _fetchEntityStart = () => _fetchEntity({
                isFetching: true
            }),
            _fetchEntitySuccess = () => _fetchEntity({
                isFetching:  false,
                lastUpdated: action.receivedAt,
                data:        this.transformEntitiesFn(action.entity)
            });


        switch (action.type) {
        case this.actionTypes.INVALIDATE:
            return {
                ...this.getInitialState(),
                didInvalidate: true
            };
        case this.actionTypes.STORE_ERROR:
            return action.launchedAction ? {
                ..._state,
                [action.launchedAction]: false,
                error:                   action.error
            } : {
                ..._state,
                error: action.error
            };
        case this.actionTypes.CLEAR_ERROR:

            return _state.error === null ? _state : {
                ..._state,
                error: null
            };

        case this.actionTypes.ENTITIES_ERROR:

            return {
                ..._state,
                cache: {
                    ..._state.cache,
                    entities: {
                        ..._state.cache.entities,
                        [action.key]: {
                            ..._state.cache.entities[action.key],
                            ...StoreModuleUtils.getEntitiesState(),
                            error: action.error
                        }
                    }
                }
            };

        case this.actionTypes.CLEAR_ENTITIES_ERROR:

            return _state.cache.entities[action.key] && _state.cache.entities[action.key].error !== null ? {
                ..._state,
                cache: {
                    ..._state.cache,
                    entities: {
                        ..._state.cache.entities,
                        [action.key]: {
                            ..._state.cache.entities[action.key],
                            error: null
                        }
                    }
                }
            } : _state;

        case this.actionTypes.CLEAR_ENTITY_ERROR:

            return _state.cache.entity[action.id] && _state.cache.entity[action.id].error !== null ? {
                ..._state,
                cache: {
                    ..._state.cache,
                    entity: {
                        ..._state.cache.entity,
                        [action.id]: {
                            ..._state.cache.entity[action.id],
                            error: null
                        }
                    }
                }
            } : _state;

        case this.actionTypes.ENTITY_ERROR:
            return {
                ..._state,
                cache: {
                    ..._state.cache,
                    entity: {
                        ..._state.cache.entity,
                        [action.id]: {
                            ..._state.cache.entity[action.id],
                            ...StoreModuleUtils.getEntityState(),
                            error: action.error
                        }
                    }
                }
            };

        case this.actionTypes.SET_ENTITIES:
            return _setEntities();
        case this.actionTypes.FETCH_START:
            return _fetchEntitiesStart();
        case this.actionTypes.FETCH_SUCCESS:
            return _fetchEntitiesSuccess();
        case this.actionTypes.FETCH_INSTANCE_START:
            return _fetchEntityStart();
        case this.actionTypes.FETCH_INSTANCE_SUCCESS:
            return _fetchEntitySuccess();
        case this.actionTypes.SET_ID_CACHE:
            return {
                ..._state,
                ...action.cache
            };
        case this.actionTypes.CREATE_START:
            return {
                ..._state,
                error:      NOT_ERROR,
                isCreating: true
            };
        case this.actionTypes.CREATE_SUCCESS:
            return {
                ..._state,
                ...this.getInitialState(),
                didInvalidate: true,
                error:         NOT_ERROR
            };
        case this.actionTypes.UPDATE_START:
            return {
                ..._state,
                isUpdating: true,
                error:      NOT_ERROR
            };
        case this.actionTypes.UPDATE_SUCCESS:
            return {
                ..._state,
                ...this.getInitialState(),
                didInvalidate: true,
                error:         NOT_ERROR
            };
        case this.actionTypes.DELETE_START:
            return {
                ..._state,
                error:      NOT_ERROR,
                isDeleting: true
            };
        case this.actionTypes.DELETE_SUCCESS:
            return {
                ..._state,
                ...this.getInitialState(),
                didInvalidate: true,
                error:         NOT_ERROR
            };
        case 'LOGOUT_SUCCESS':
            return this.getInitialState();

        default:
            return this.reducerExtraActions(_state, action);
        }
    }

}





// WEBPACK FOOTER //
// ./src/util/storeModule/storeModuleUtils.js